import { useLocation, Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Home, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const NotFound = () => {
  const location = useLocation();
  const { user, loading } = useAuth();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  // Wait for auth so we don't bounce a logged-in user to the public landing.
  if (loading) {
    return (
      <div className="theme-auth flex min-h-screen items-center justify-center bg-background p-4">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  // Recover stale challenge deep-links (the most common 404 trigger after
  // backgrounding the app, refreshing, or following a finished challenge URL).
  if (user && location.pathname.startsWith("/challenge/")) {
    toast.info("This challenge page expired or changed. We've taken you back to Challenge Center.");
    return <Navigate to="/challenges" replace />;
  }

  // Authenticated users hitting any other unknown in-app route: send them home
  // rather than to the public landing/raw 404.
  const looksLikeAppRoute =
    location.pathname.startsWith("/lesson/") ||
    location.pathname.startsWith("/course/") ||
    location.pathname.startsWith("/assessment/") ||
    location.pathname.startsWith("/admin/");
  if (user && looksLikeAppRoute) {
    toast.info("That page is no longer available. Returning to your dashboard.");
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <SEO
        title="Page Not Found — HeirWise"
        description="The HeirWise page you're looking for doesn't exist or has moved. Head back to the homepage to keep learning."
        path="/404"
      />
      <div className="theme-auth flex min-h-screen items-center justify-center bg-background p-4">
      <div className="glass-card glow-border max-w-md w-full p-8 text-center space-y-6">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto">
          <Home className="w-8 h-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h1 className="font-heading font-bold text-5xl text-gradient-blue">404</h1>
          <p className="text-lg text-foreground font-heading font-semibold">Page Not Found</p>
          <p className="text-sm text-muted-foreground">
            The page you're looking for doesn't exist or has been moved.
          </p>
        </div>
        <Link to={user ? "/dashboard" : "/"}>
          <Button className="w-full gap-2">
            <ArrowLeft className="w-4 h-4" />
            {user ? "Return to Dashboard" : "Return Home"}
          </Button>
        </Link>
      </div>
      </div>
    </>
  );
};

export default NotFound;
